import useInfinityScroll from '@/common/hooks/useInfinityScroll';
import { InsuranceABV } from '@/common/types/SelectionOptions.type';
import useVehicleInformationSelections from '@/modules/adjusterReportModules/IncidentParticipants/data/VehicleInformationSelections/useVehicleInformationSelections';
import { VehicleInformationPartOneFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { getBodyVehicleInformationOption } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyVehicleInformation/getBodyVehicleInformationOption';
import { patchBodyPartOneVehicleInformationOption } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyPartOneVehicleInformation/patchBodyPartOneVehicleInformationOption';
import { BodyVehicleInformationPartOneFormType } from '@/modules/adjusterReportModules/Insurer-information/types/BodyVehicleInformationType';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

const useBodyPartOneHook = () => {
  const methods = useForm<BodyVehicleInformationPartOneFormType>();
  const { id } = useParams();
  const [name, setName] = useState<string | null>(null);
  const VehicleTypeWatch = useWatch({
    control: methods.control,
    name: 'vehicle_type',
  });

  const VehiclePlateDesignWatch = useWatch({
    control: methods.control,
    name: 'vehicle_plate_design',
  });
  const VehiclePlateTypeWatch = useWatch({
    control: methods.control,
    name: 'vehicle_plate_type',
  });
  const { data: fileDetail } = useQuery(fileDetailQueryOption({ id: +id! }));
  const navigate = useNavigate();

  const vehicleSystemsDependenciesMemo = useMemo(
    () => ({
      insurance: fileDetail?.insurance as InsuranceABV,
      type: VehicleTypeWatch?.value?.toString()!,
      name: name!,
    }),
    [fileDetail?.insurance, VehicleTypeWatch?.value?.toString()!, name!],
  );
  const vehicleUsesDependenciesMemo = useMemo(
    () => ({
      insurance: fileDetail?.insurance as InsuranceABV,
      type: VehicleTypeWatch?.value?.toString()!,
    }),
    [fileDetail?.insurance, VehicleTypeWatch?.value?.toString()],
  );
  const plateTypesDependenciesMemo = useMemo(
    () => ({
      type: VehicleTypeWatch?.value?.toString() ?? '1',
      insurance: fileDetail?.insurance as InsuranceABV,
    }),
    [VehicleTypeWatch?.value?.toString()],
  );
  const plateDesignDependenciesMemo = useMemo(
    () => ({
      type: VehicleTypeWatch?.value?.toString() ?? '1',
      insurance: fileDetail?.insurance as InsuranceABV,
    }),
    [VehicleTypeWatch?.value?.toString()],
  );
  const plateSamplesDependenciesMemo = useMemo(
    () => ({
      type: VehicleTypeWatch?.value?.toString() ?? '',
      insurance: fileDetail?.insurance as InsuranceABV,
      plate_type: VehiclePlateTypeWatch?.value?.toString() ?? '',
      plate_design: VehiclePlateDesignWatch?.value.toString() ?? '',
    }),
    [
      VehicleTypeWatch?.value?.toString(),
      VehiclePlateTypeWatch?.value?.toString(),
      VehiclePlateDesignWatch?.value?.toString(),
    ],
  );
  const data = useVehicleInformationSelections({
    vehicleSystemsDependencies: vehicleSystemsDependenciesMemo,
    vehicleUsesDependencies: vehicleUsesDependenciesMemo,
    plateTypesDependencies: plateTypesDependenciesMemo,
    plateDesignDependencies: plateDesignDependenciesMemo,
    plateSamplesDependencies: plateSamplesDependenciesMemo,
  });
  const {
    mutate: mutatePatchPartOneVehicleInformation,
    isPending: patchPending,
  } = useMutation(patchBodyPartOneVehicleInformationOption());

  const { observerRefCallback } = useInfinityScroll(
    data?.fetchVehicleSystemsNextPage,
  );

  const { data: vehicleInformationData } = useQuery(
    getBodyVehicleInformationOption({
      id: +id!,
    }),
  );

  const submitHandler = (data: VehicleInformationPartOneFormType) => {
    mutatePatchPartOneVehicleInformation(
      {
        id: +id!,
        data: {
          vehicle_type: data?.vehicle_type?.value!,
          vehicle_model: data?.vehicle_model?.value!,
          vehicle_use: data?.vehicle_use?.value!,
          vehicle_plate_type: data?.vehicle_plate_type?.value!,
          vehicle_plate_design: data?.vehicle_plate_design?.value!,
          vehicle_plate_sample: data?.vehicle_plate_sample?.value!,
          vehicle_plate: data?.vehicle_plate?.plateString!,
        },
      },
      {
        onSuccess: () => {
          navigate(
            ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
              id: id!,
            }).VEHICLE_INFORMATION.partTwo,
            { replace: true },
          );
        },
      },
    );
  };

  useEffect(() => {
    if (vehicleInformationData) {
      methods.reset({
        vehicle_model: vehicleInformationData?.vehicle_model
          ? {
              label: vehicleInformationData?.vehicle_model?.name,
              value: vehicleInformationData?.vehicle_model?.id,
            }
          : null,
        vehicle_plate_design: vehicleInformationData?.vehicle_plate_design
          ? {
              label: vehicleInformationData?.vehicle_plate_design?.fa_const,
              value: vehicleInformationData?.vehicle_plate_design?.const,
            }
          : {
              label: 'طرح کشوری',
              value: 'country',
            },
        vehicle_plate_sample: vehicleInformationData?.vehicle_plate_sample
          ? {
              label: vehicleInformationData?.vehicle_plate_sample?.title,
              value: vehicleInformationData?.vehicle_plate_sample?.id,
            }
          : null,
        vehicle_plate: {
          plate: {
            vehicle_plate_bottom: vehicleInformationData?.vehicle_plate_bottom,
            vehicle_plate_top: vehicleInformationData?.vehicle_plate_top,
            iran: vehicleInformationData?.vehicle_plate_iran,
            left: vehicleInformationData?.vehicle_plate_left,
            right: vehicleInformationData?.vehicle_plate_right,
            letter: {
              label: vehicleInformationData?.vehicle_plate_letter?.letter,
              value:
                vehicleInformationData?.vehicle_plate_letter?.id?.toString(),
            },
          },
          plateString: vehicleInformationData?.vehicle_plate,
        },
        vehicle_plate_type: vehicleInformationData?.vehicle_plate_type
          ? {
              label: vehicleInformationData?.vehicle_plate_type?.name,
              value: vehicleInformationData?.vehicle_plate_type?.id,
            }
          : vehicleInformationData?.vehicle_type?.id === 1
            ? {
                label: 'شخصی',
                value: 77,
              }
            : null,
        vehicle_type: vehicleInformationData?.vehicle_type
          ? {
              label: vehicleInformationData?.vehicle_type?.fa_name,
              value: vehicleInformationData?.vehicle_type?.id,
            }
          : {
              label: 'سواری',
              value: 1,
            },
        vehicle_use: vehicleInformationData?.vehicle_use
          ? {
              label: vehicleInformationData?.vehicle_use?.name,
              value: vehicleInformationData?.vehicle_use?.id,
            }
          : null,
      });
    }
  }, [vehicleInformationData]);

  useEffect(() => {
    if (methods?.formState?.isDirty) {
      methods.setValue('vehicle_plate_sample', null);
    }
  }, [
    VehiclePlateTypeWatch?.value?.toString(),
    VehiclePlateDesignWatch?.value?.toString(),
  ]);

  useEffect(() => {
    if (methods?.formState?.isDirty) {
      methods.setValue('vehicle_use', null);
      methods.setValue('vehicle_model', null);
      methods.setValue('vehicle_plate_type', null);
      methods.setValue('vehicle_plate_design', null);
      methods.setValue('vehicle_plate_sample', null);
      methods.setValue('vehicle_plate.plate.vehicle_plate_bottom', '');
      methods.setValue('vehicle_plate.plate.iran', '');
      methods.setValue('vehicle_plate.plate.left', '');
      methods.setValue('vehicle_plate.plate.letter', undefined);
      methods.setValue('vehicle_plate.plate.right', undefined);
      methods.setValue('vehicle_plate.plate.vehicle_plate_top', undefined);
      methods.setValue('vehicle_plate', {
        plate: {
          vehicle_plate_bottom: '',
          iran: '',
          left: '',
          letter: {
            label: '',
            value: '',
          },
          right: '',
          vehicle_plate_top: '',
        },
        plateString: '',
      });
    }
  }, [VehicleTypeWatch?.value?.toString()]);

  useEffect(() => {
    if (methods?.formState?.isDirty) {
      methods.setValue('vehicle_plate_sample', null);
    }
  }, [
    VehiclePlateTypeWatch?.value?.toString(),
    VehiclePlateDesignWatch?.value?.toString(),
  ]);
  useEffect(() => {
    if (methods?.formState?.isDirty) {
      methods.setValue('vehicle_plate.plate.vehicle_plate_bottom', '');
      methods.setValue('vehicle_plate.plate.iran', '');
      methods.setValue('vehicle_plate.plate.left', '');
      methods.setValue('vehicle_plate.plate.letter', undefined);
      methods.setValue('vehicle_plate.plate.right', undefined);
      methods.setValue('vehicle_plate.plate.vehicle_plate_top', undefined);
      methods.setValue('vehicle_plate', {
        plate: {
          vehicle_plate_bottom: '',
          iran: '',
          left: '',
          letter: {
            label: '',
            value: '',
          },
          right: '',
          vehicle_plate_top: '',
        },
        plateString: '',
      });
    }
  }, [VehiclePlateDesignWatch?.value?.toString()]);
  return {
    methods,
    data,
    observerRefCallback,
    vehicleInformationData,
    submitHandler,
    VehicleTypeWatch,
    VehiclePlateDesignWatch,
    VehiclePlateTypeWatch,
    setName,
    patchPending,
  };
};
export default useBodyPartOneHook;
